/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Calendar, FileText, Heart, UserRound, Tag } from 'lucide-react';

export const relationIcon = (rel = '') => {
  const r = (rel || '').toLowerCase();
  if (r.includes('figli') || r.includes('bimb')) return Heart;
  if (r.includes('coniug') || r.includes('marito') || r.includes('moglie') || r.includes('compagn')) return Users;
  return UserRound;
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};

export const AppointmentRow = ({ a, onAssign }) => (
  <div className="flex items-center gap-2 rounded-xl border bg-card p-2.5 text-xs" data-testid={`fd-appt-${a.id}`}>
    <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
      <Calendar className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold truncate">{a.doctor_name || '—'}</div>
      <div className="text-muted-foreground">{fmtDate(a.scheduled_at)} · <span className="capitalize">{a.status || 'scheduled'}</span></div>
    </div>
    {onAssign && (
      <Button size="sm" variant="ghost" onClick={() => onAssign('appointment', a.id)} data-testid={`fd-assign-appt-${a.id}`}>
        <Tag className="h-3 w-3" /> Assegna
      </Button>
    )}
  </div>
);

export const DocRow = ({ d, onAssign }) => (
  <div className="flex items-center gap-2 rounded-xl border bg-card p-2.5 text-xs" data-testid={`fd-doc-${d.id}`}>
    <div className="h-8 w-8 rounded-lg bg-emerald-500/15 text-emerald-600 flex items-center justify-center flex-shrink-0">
      <FileText className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold truncate">
        <span className="uppercase text-[10px] tracking-wider mr-1">{d.doc_type || 'doc'}</span>
        {d.diagnosis || d.professional_name || '—'}
      </div>
      <div className="text-muted-foreground">{fmtDate(d.signed_at || d.created_at)}</div>
    </div>
    {onAssign && (
      <Button size="sm" variant="ghost" onClick={() => onAssign('fse_document', d.id)} data-testid={`fd-assign-doc-${d.id}`}>
        <Tag className="h-3 w-3" /> Assegna
      </Button>
    )}
  </div>
);

export const MemberSummaryCard = ({ member, isSelf, active, onClick }) => {
  const Icon = isSelf ? UserRound : relationIcon(member.relation);
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`text-left rounded-2xl border p-3 transition-colors ${
        active ? 'bg-primary/10 border-primary/50 shadow-inner' : 'bg-secondary/30 border-border/60 hover:bg-secondary/50'
      }`}
      data-testid={`fd-member-card-${member.key}`}
    >
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{member.full_name}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {isSelf ? 'Titolare del bundle' : `${member.relation || 'familiare'}${member.year_of_birth ? ` · ${member.year_of_birth}` : ''}`}
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-1.5 text-[10px]">
        <Badge variant="outline" className="gap-1"><Calendar className="h-3 w-3" /> {member.counts.appointments}</Badge>
        {member.counts.upcoming > 0 && (
          <Badge className="bg-primary/15 text-primary border-0">{member.counts.upcoming} in arrivo</Badge>
        )}
        <Badge variant="outline" className="gap-1"><FileText className="h-3 w-3" /> {member.counts.documents}</Badge>
      </div>
    </motion.button>
  );
};
